/**
 * S10 — Utilitaires de résolution des tarifs dégressifs (côté client)
 * Miroir du backend PriceTierService.resolvePrice (même algo)
 */

import type { PricingMode, PriceTier } from '@/types/models';

export interface TierResolution {
  effectiveUnitPrice: number;
  tierApplied: boolean;
  tierDetail?: string;
}

export interface GraduatedBracket {
  from: number;
  to: number;
  quantity: number;
  unitPrice: number;
  bracketTotal: number;
}

/**
 * Résoudre le prix effectif pour une quantité donnée
 * Identique à PriceTierService.resolvePrice (backend)
 */
export function resolveTierPrice(
  quantity: number,
  basePrice: number,
  pricingMode: PricingMode,
  tiers: Array<{ minQuantity: number; unitPrice: number }>
): TierResolution {
  if (!tiers || tiers.length === 0 || quantity <= 0) {
    return { effectiveUnitPrice: basePrice, tierApplied: false };
  }

  if (pricingMode === 'VOLUME') {
    return resolveVolumePrice(quantity, basePrice, tiers);
  }

  return resolveGraduatedPrice(quantity, basePrice, tiers);
}

/**
 * VOLUME : toutes les unités au prix du palier atteint
 */
function resolveVolumePrice(
  quantity: number,
  basePrice: number,
  tiers: Array<{ minQuantity: number; unitPrice: number }>
): TierResolution {
  const sorted = [...tiers].sort((a, b) => b.minQuantity - a.minQuantity);

  for (const tier of sorted) {
    if (quantity >= tier.minQuantity) {
      return {
        effectiveUnitPrice: tier.unitPrice,
        tierApplied: true,
        tierDetail: `Palier VOLUME: ${quantity} >= ${tier.minQuantity} → ${tier.unitPrice}€/u`,
      };
    }
  }

  return { effectiveUnitPrice: basePrice, tierApplied: false };
}

/**
 * GRADUATED : chaque tranche a son prix (prix moyen pondéré)
 */
function resolveGraduatedPrice(
  quantity: number,
  basePrice: number,
  tiers: Array<{ minQuantity: number; unitPrice: number }>
): TierResolution {
  const brackets = resolveGraduatedBreakdown(quantity, tiers);

  if (brackets.length === 0) {
    return { effectiveUnitPrice: basePrice, tierApplied: false };
  }

  const totalAmount = brackets.reduce((sum, b) => sum + b.bracketTotal, 0);
  const effectivePrice = quantity > 0 ? totalAmount / quantity : basePrice;

  return {
    effectiveUnitPrice: parseFloat(effectivePrice.toFixed(4)),
    tierApplied: true,
    tierDetail: brackets
      .map(b => `${b.from}-${b.to}: ${b.quantity}×${b.unitPrice}€`)
      .join(' | '),
  };
}

/**
 * Détail des tranches pour le mode GRADUATED
 */
export function resolveGraduatedBreakdown(
  quantity: number,
  tiers: Array<{ minQuantity: number; unitPrice: number }>
): GraduatedBracket[] {
  if (quantity <= 0 || !tiers || tiers.length === 0) {
    return [];
  }

  const sorted = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity);
  const brackets: GraduatedBracket[] = [];
  let remaining = quantity;

  for (let i = 0; i < sorted.length && remaining > 0; i++) {
    const tier = sorted[i];
    const nextTier = sorted[i + 1];
    const from = tier.minQuantity;
    const to = nextTier ? nextTier.minQuantity : Infinity;

    const bracketCapacity = to === Infinity ? remaining : to - from;
    const bracketQty = Math.min(remaining, bracketCapacity);

    if (bracketQty > 0) {
      brackets.push({
        from,
        to: to === Infinity ? from + bracketQty : to,
        quantity: bracketQty,
        unitPrice: tier.unitPrice,
        bracketTotal: bracketQty * tier.unitPrice,
      });
      remaining -= bracketQty;
    }
  }

  return brackets;
}
